import { defineNuxtPlugin } from 'nuxt/app'
import buildEventsQuery from '~/queries/events'
import buildMembershipArticlesQuery from '~/queries/membership-articles'
import buildAnalysisArticlesQuery from '~/queries/analysis-articles'
import buildAnalysisArticleDetailBySlugQuery from '~/queries/analysis-article-detail-by-slug'
import buildAnalysisArticleDetailByIdQuery from '~/queries/analysis-article-detail-by-id'
import buildAnalysisArticlesByDateQuery from '~/queries/analysis-articles-by-date'
import buildAnalysisCategoriesQuery from '~/queries/analysis-articles-categories'
import { buildMarketInsightsQuery } from '~/queries/market-insights'
import buildNewsArticlesQuery from '~/queries/news-articles'
import buildVideosQuery from '~/queries/videos'
import buildMarketInsightsVideosQuery from '~/queries/market-insights-videos'
import buildVideosByTagsQuery from '~/queries/videos-by-tags'
import getAuthenticatedUserDataQuery from '@/queries/authenticated-user.gql'
import {
  getAdaptedVideos,
  getAdaptedPosts,
  getAdaptedArticles,
  getAdaptedMarketInsights,
} from '~/utils/adapters'
import buildEventsWithDateQuery from '~/queries/events-with-date-query'
import { buildPortalArticlesQuery } from '~/queries/portal-articles'
import type { TEventResponse } from '~/src/types/events'
import { processEvents } from '~/src/utils/events'
import type { TGraphQlResponse } from '~/src/types/graphql'

export default defineNuxtPlugin({
  name: 'graphql-client',
  enforce: 'post',
  dependsOn: ['bmAuth'],
  setup(nuxtApp) {
    const { $bmAuth, $config }: any = nuxtApp

    const includeNonPublished = $config.public.isPreviewSite

    const graphqlClient = $config.public.isPreviewSite
      ? $bmAuth.getAuthenticatedClient()
      : $bmAuth.getPublicGraphqlClient()

    const getAnalysisArticles = async ({
      service,
      first,
      after,
      search,
      categories,
    }) => {
      try {
        const response = await $bmAuth.getPublicGraphqlClient().query({
          query: buildAnalysisArticlesQuery({ service, includeNonPublished }),
          variables: {
            first,
            after,
            search,
            categories,
          },
        })

        response.data.articles.nodes = getAdaptedPosts(response.data.articles)
        return response
      } catch (error) {
        logError(error)
        return {
          data: {
            articles: {
              nodes: [],
            },
          },
        }
      }
    }

    const getAnalysisArticleDetail = async ({
      service,
      slug,
      usePublicClient = false,
    }) => {
      try {
        const hasSlug = slug.match(/[a-zA-Z]/)
        const isDraft = !hasSlug

        if (isDraft && !$config.public.isPreviewSite) {
          return null
        }

        const graphqlClient = usePublicClient
          ? $bmAuth.getPublicGraphqlClient()
          : $bmAuth.getGraphqlClient()

        if (hasSlug) {
          return graphqlClient.query({
            query: buildAnalysisArticleDetailBySlugQuery({ service }),
            variables: { slug },
          })
        }

        return graphqlClient.query({
          query: buildAnalysisArticleDetailByIdQuery({ service }),
          variables: { id: slug },
        })
      } catch (error) {
        logError(error)
        return null
      }
    }

    const getEvents = async () => {
      try {
        const response: TGraphQlResponse = await graphqlClient.query({
          query: buildEventsQuery({ includeNonPublished }),
        })
        const events = response.data.events as TEventResponse
        return processEvents(events.nodes) || []
      } catch (error) {
        logError(error)
        return []
      }
    }

    const getEventsWithDateQuery = async () => {
      try {
        const response: TGraphQlResponse = await graphqlClient.query({
          query: buildEventsWithDateQuery({ includeNonPublished }),
        })

        const events = response.data.events as TEventResponse
        return processEvents(events.nodes) || []
      } catch (error) {
        logError(error)
        return []
      }
    }

    const getMembershipArticles = async ({
      first,
      after,
      search,
      showOnlyFeaturedPosts = false,
      categoryName = '',
    }: any) => {
      try {
        const response = await graphqlClient.query({
          query: buildMembershipArticlesQuery({
            includeNonPublished,
          }),
          variables: {
            first,
            after,
            search,
            showOnlyFeaturedPosts,
            categoryName,
          },
        })

        response.data.memberships.nodes = getAdaptedPosts(
          response.data.memberships,
        )
        return response
      } catch (error) {
        logError(error)
        return {
          data: {
            memberships: {
              nodes: [],
            },
          },
        }
      }
    }

    const getAnalysisArticlesByDate = async ({
      service,
      categories,
      order,
      day,
      month,
      year,
      orderDate,
    }) => {
      try {
        const response = await $bmAuth.getGraphqlClient().query({
          query: buildAnalysisArticlesByDateQuery({
            service,
            includeNonPublished,
            orderDate,
          }),
          variables: {
            categories,
            order,
            day,
            month,
            year,
          },
        })
        response.data.articles.nodes = getAdaptedPosts(response.data?.articles)
        return response
      } catch (error) {
        logError(error)
        return {
          data: {
            articles: {
              nodes: [],
            },
          },
        }
      }
    }

    const getAnalysisCategories = async ({ service }) => {
      try {
        return $bmAuth.getPublicGraphqlClient().query({
          query: buildAnalysisCategoriesQuery({
            service,
          }),
        })
      } catch (error) {
        logError(error)
        return {
          data: {
            categories: {
              nodes: [],
            },
          },
        }
      }
    }

    const getVideos = async (product) => {
      try {
        const videos = await $bmAuth.getGraphqlClient().query({
          query: buildVideosQuery(),
          variables: {
            first: 10,
            after: null,
            categoryName: product,
          },
        })
        return getAdaptedVideos(videos.data.videos.nodes)
      } catch (error) {
        logError(error)
        return []
      }
    }

    const getMarketInsightsVideos = async (
      webinarsTags,
      oneToOneTags,
      interviewsTags,
      categories,
    ) => {
      try {
        const { data } = await $bmAuth.getGraphqlClient().query({
          query: buildMarketInsightsVideosQuery(
            webinarsTags,
            oneToOneTags,
            interviewsTags,
          ),
          variables: { categories },
        })

        Object.keys(data).forEach((key) => {
          data[key].nodes = getAdaptedVideos(data[key].nodes)
        })
        return data
      } catch (error) {
        logError(error)
        return {
          webinars: { nodes: [] },
          oneToOne: { nodes: [] },
          interviews: { nodes: [] },
        }
      }
    }

    const getVideosByTags = async (
      first,
      tags,
      categories,
      excludeTags = false,
      after = null,
    ) => {
      try {
        const { data } = await $bmAuth.getGraphqlClient().query({
          query: buildVideosByTagsQuery(first, tags, excludeTags, after),
          variables: { categories },
        })
        data.videos.nodes = getAdaptedVideos(data.videos.nodes)
        return data.videos
      } catch (error) {
        logError(error)
        return {
          nodes: [],
          pageInfo: {
            hasNextPage: false,
            endCursor: null,
          },
        }
      }
    }

    const getMarketInsightsArticles = async ({ market, categories = null }) => {
      try {
        const { data } = await $bmAuth.getPublicGraphqlClient().query({
          query: buildMarketInsightsQuery({ market, categories }),
        })
        const marketInsights = getAdaptedMarketInsights(data)
        const articlesMarket = marketInsights?.articles.map((article) => ({
          ...article,
          url: article.url
            ? `${$config.public.sourceUrl}/${article.url}`
            : null,
        }))
        return { ...marketInsights, articles: articlesMarket }
      } catch (error) {
        logError(error)
        return {
          articles: [],
          videos: {
            webinars: { nodes: [] },
            oneToOne: { nodes: [] },
            interviews: { nodes: [] },
          },
        }
      }
    }

    const getPortalArticles = async ({ market, categories = null }) => {
      try {
        const { data } = await $bmAuth.getPublicGraphqlClient().query({
          query: buildPortalArticlesQuery({ market, categories }),
        })
        const marketInsights = getAdaptedMarketInsights(data)
        const articlesMarket = marketInsights?.articles.map((article) => ({
          ...article,
          url: article.url
            ? `${$config.public.sourceUrl}/${article.url}`
            : null,
        }))
        return { ...marketInsights, articles: articlesMarket }
      } catch (error) {
        logError(error)
        return {
          articles: [],
          videos: {
            webinars: { nodes: [] },
            oneToOne: { nodes: [] },
            interviews: { nodes: [] },
          },
        }
      }
    }

    const getNewsArticles = async ({ market, categories = null }) => {
      try {
        const { data } = await $bmAuth.getPublicGraphqlClient().query({
          query: buildNewsArticlesQuery({ market, categories }),
          variables: {
            first: 12,
          },
        })

        return getAdaptedArticles(data.memberships.nodes, 'dd MMM yyyy').map(
          (article) => ({
            ...article,
            url: article.url
              ? `${$config.public.sourceUrl}/${article.url}`
              : null,
          }),
        )
      } catch (error) {
        logError(error)
        return []
      }
    }

    const getAuthenticatedUserData = async () => {
      try {
        const { data } = await $bmAuth.getAuthenticatedClient().query({
          query: getAuthenticatedUserDataQuery,
        })

        return {
          email: data.viewer?.email,
          company: data.viewer?.userData?.company,
        }
      } catch (error) {
        logError(error)
        return {}
      }
    }

    return {
      provide: {
        graphqlClient: {
          getAnalysisArticleDetail,
          getAnalysisArticles,
          getAnalysisArticlesByDate,
          getAnalysisCategories,
          getEvents,
          getMembershipArticles,
          getPortalArticles,
          getVideos,
          getMarketInsightsArticles,
          getMarketInsightsVideos,
          getNewsArticles,
          getVideosByTags,
          getEventsWithDateQuery,
          getAuthenticatedUserData,
        },
      },
    }
  },
})
